const STABLE_CODE = /^[A-Za-z][A-Za-z0-9_.-]{0,159}$/u;

export type StoreOverviewQueryFailureKind =
  | "forbidden"
  | "hard_failure"
  | "owner_missing";

export interface StoreOverviewQueryFailure {
  code: string;
  httpStatus: number | null;
  kind: StoreOverviewQueryFailureKind;
}

export const STORE_OVERVIEW_UNAVAILABLE_FAILURE: StoreOverviewQueryFailure = {
  code: "store_overview_unavailable",
  httpStatus: null,
  kind: "hard_failure",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asStableCode(value: string) {
  const code = value.trim();
  return STABLE_CODE.test(code) ? code : null;
}

export function readStoreOverviewHttpStatus(error: unknown) {
  if (!isRecord(error)) return null;
  if (typeof error.status === "number" && Number.isInteger(error.status)) {
    return error.status;
  }
  if (
    typeof error.originalStatus === "number" &&
    Number.isInteger(error.originalStatus)
  ) {
    return error.originalStatus;
  }
  return null;
}

function readApiErrorCode(error: unknown) {
  if (!isRecord(error)) return null;
  const data = error.data;
  if (typeof data === "string") {
    const fromData = asStableCode(data);
    if (fromData) return fromData;
  }
  if (isRecord(data)) {
    if (typeof data.error === "string") {
      const fromError = asStableCode(data.error);
      if (fromError) return fromError;
    }
    if (isRecord(data.error) && typeof data.error.code === "string") {
      const fromNested = asStableCode(data.error.code);
      if (fromNested) return fromNested;
    }
  }
  if (typeof error.message === "string") {
    const fromMessage = asStableCode(error.message);
    if (fromMessage) return fromMessage;
  }
  if (typeof error.error === "string") {
    return asStableCode(error.error);
  }
  return null;
}

function fallbackCode(error: unknown, httpStatus: number | null) {
  if (!isRecord(error)) {
    return httpStatus
      ? `http_${httpStatus}`
      : STORE_OVERVIEW_UNAVAILABLE_FAILURE.code;
  }
  if (error.status === "FETCH_ERROR") return "store_overview_network_error";
  if (error.status === "TIMEOUT_ERROR") return "store_overview_timeout";
  if (error.status === "PARSING_ERROR" || error.status === "CUSTOM_ERROR") {
    return "store_overview_response_invalid";
  }
  return httpStatus
    ? `http_${httpStatus}`
    : STORE_OVERVIEW_UNAVAILABLE_FAILURE.code;
}

export function readStoreOverviewQueryFailure(
  error: unknown,
  options: { skipped: boolean },
): StoreOverviewQueryFailure | null {
  if (options.skipped) {
    return {
      code: "store_overview_owner_missing",
      httpStatus: null,
      kind: "owner_missing",
    };
  }
  if (error == null) return null;
  const httpStatus = readStoreOverviewHttpStatus(error);
  const code = readApiErrorCode(error) ?? fallbackCode(error, httpStatus);
  if (httpStatus === 403) {
    return { code, httpStatus, kind: "forbidden" };
  }
  return { code, httpStatus, kind: "hard_failure" };
}

export function resolveStoreOverviewQueryView<T>(
  query: {
    currentData?: T;
    error?: unknown;
    isError: boolean;
    isFetching: boolean;
    isLoading: boolean;
    isUninitialized: boolean;
  },
  options: { skipped: boolean },
) {
  const failure = readStoreOverviewQueryFailure(query.error, options);
  const overview = failure || query.isError ? undefined : query.currentData;
  const showLoading =
    !options.skipped &&
    (query.isUninitialized ||
      query.isLoading ||
      (query.isFetching && !overview && !query.isError));
  return { failure, overview, showLoading };
}
